import { AuraBaseDesignTokens, AuraBaseTokenSections } from "@primeuix/themes/aura/base";
import type { ColorScale } from "@primeuix/styled";

// Legacy "old cleverly" palette (pre-rebrand blue brand) — restored from git
// (49300725a^) as a parallel theme. Only the palette differs from basetokens.ts;
// sand/grey/wine aliases below map the Cleverly DS neutral/error scale names
// (used by component tokens) onto the old ramps so both presets share one
// component-token layer.
type LegacyPrimitive = AuraBaseTokenSections.Primitive & {
    sand?: ColorScale;
    grey?: ColorScale;
    wine?: ColorScale;
};

export const primitive: LegacyPrimitive = {
    borderRadius: {
        none: '0',
        xs: '4px',
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px'
    },

    // HeyTimi Chalkboard + Palette Blue scale (all HeyTimi blues)
    blue: {
        50: '#F0F9FF',
        100: '#E0F2FE', 
        200: '#BAE6FD',
        300: '#7DD3FC',
        400: '#67c0e9',  // htColorChalkboardLight, htColorSunLight, htColorSalmonLight
        500: '#35ABE2',  // htColorChalkboardMedium, htColorSunDark, htColorSalmonDark
        600: '#2196F3',  // exact blue from your palette
        700: '#12597a',  // htColorChalkboardDarker
        800: '#0F4C75',
        900: '#1C1C1C',  // htColorChalkboardDark (moved from gray to blue scale)
        950: '#0C1B2E'
    },

    // HeyTimi Sun + Palette Yellow scale
    yellow: {
        50: '#FEFCE8',
        100: '#FEF3C7',
        200: '#FEE68C',
        300: '#FCD34D',
        400: '#FCE02F',  // Close to sunlight 
        500: '#FBC02D',  // exact yellow from your palette
        600: '#D97706',
        700: '#B45309',
        800: '#92400E',
        900: '#78350F',
        950: '#451A03'
    },

    // Success Green scale + Palette
    green: {
        50: '#F0FDF4',
        100: '#DCFCE7',
        200: '#BBF7D0',
        300: '#86EFAC',
        400: '#6FDCBB',  // statesuccess
        500: '#4caf50',  // exact green from your palette
        600: '#059669',
        700: '#047857',
        800: '#065F46',
        900: '#064E3B',
        950: '#022C22'
    },

    // Error Red scale + Palette (multiple reds)
    red: {
        50: '#FEF2F2',
        100: '#FEE2E2',
        200: '#FECACA',
        300: '#FCA5A5',
        400: '#FF3F61',  // stateerror (original HeyTimi red)
        500: '#FF4032',  // exact red from your palette
        600: '#DC2626',
        700: '#B91C1C',
        800: '#991B1B',
        900: '#7F1D1D',
        950: '#450A0A'
    },

    // Neutral grays (based on your shade colors)
    gray: {
        50: '#FEFAF7',   // shadeextralight
        100: '#F6F0EC',  // shadelight
        200: '#F1EAE4',  // shademedium
        300: '#EAE3DD',  // shadedark
        400: '#DED4CE',  // shadeextradark
        500: '#9CA3AF',
        600: '#6B7280',
        700: '#4B5563',
        800: '#3B3B3B',  // textsecondary
        900: '#1C1C1C',  // textprimary
        950: '#111827'
    },

    // Slate scale for additional neutrals + bluegray
    slate: {
        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#607D8B',  // exact bluegray from your palette
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A',
        950: '#020617'
    },

    // Orange scale + Palette
    orange: {
        50: '#FFF7ED',
        100: '#FFEDD5',
        200: '#FED7AA',
        300: '#FDBA74',
        400: '#FB923C',
        500: '#F57C00',  // exact orange from your palette
        600: '#EA580C',
        700: '#C2410C',
        800: '#9A3412',
        900: '#7C2D12',
        950: '#431407'
    },

    // Purple scale + Palette
    purple: {
        50: '#FAF5FF',
        100: '#F3E8FF',
        200: '#E9D5FF',
        300: '#D8B4FE',
        400: '#C084FC',
        500: '#9C27B0',  // exact purple from your palette
        600: '#7C3AED',
        700: '#6D28D9',
        800: '#5B21B6',
        900: '#4C1D95',
        950: '#2E1065'
    },

    // Teal scale + Palette
    teal: {
        50: '#F0FDFA',
        100: '#CCFBF1',
        200: '#99F6E4',
        300: '#5EEAD4',
        400: '#2DD4BF',
        500: '#009688',  // exact teal from your palette
        600: '#0D9488',
        700: '#0F766E',
        800: '#115E59',
        900: '#134E4A',
        950: '#042F2E'
    },

    // Additional palette colors as requested
    cyan: {
        50: '#ECFEFF',
        100: '#CFFAFE',
        200: '#A5F3FC',
        300: '#67E8F9',
        400: '#22D3EE',
        500: '#00BCD4',  // exact color from your palette
        600: '#0891B2',
        700: '#0E7490',
        800: '#155E75',
        900: '#164E63',
        950: '#083344'
    },

    sky: {
        50: '#F0F9FF',
        100: '#E0F2FE',
        200: '#BAE6FD',
        300: '#7DD3FC',
        400: '#38BDF8',
        500: '#0EA5E9',
        600: '#0284C7',
        700: '#0369A1',
        800: '#075985',
        900: '#0C4A6E',
        950: '#082F49'
    },

    pink: {
        50: '#FDF2F8',
        100: '#FCE7F3',
        200: '#FBCFE8',
        300: '#F9A8D4',
        400: '#F472B6',
        500: '#E91E63',  // exact color from your palette
        600: '#DB2777',
        700: '#BE185D',
        800: '#9D174D',
        900: '#831843',
        950: '#500724'
    },

    rose: {
        50: '#FFF1F2',
        100: '#FFE4E6',
        200: '#FECDD3',
        300: '#FDA4AF',
        400: '#FB7185',
        500: '#F43F5E',
        600: '#E11D48',
        700: '#BE123C',
        800: '#9F1239',
        900: '#881337',
        950: '#4C0519'
    },

    fuchsia: {
        50: '#FDF4FF',
        100: '#FAE8FF',
        200: '#F5D0FE',
        300: '#F0ABFC',
        400: '#E879F9',
        500: '#D946EF',
        600: '#C026D3',
        700: '#A21CAF',
        800: '#86198F',
        900: '#701A75',
        950: '#4A044E'
    },

    violet: {
        50: '#F5F3FF',
        100: '#EDE9FE',
        200: '#DDD6FE',
        300: '#C4B5FD',
        400: '#A78BFA',
        500: '#8B5CF6',
        600: '#7C3AED',
        700: '#6D28D9',
        800: '#5B21B6',
        900: '#4C1D95',
        950: '#2E1065'
    },

    indigo: {
        50: '#EEF2FF',
        100: '#E0E7FF',
        200: '#C7D2FE',
        300: '#A5B4FC',
        400: '#818CF8',
        500: '#3F51B5',  // exact color from your palette
        600: '#4F46E5',
        700: '#4338CA',
        800: '#3730A3',
        900: '#312E81',
        950: '#1E1B4B'
    },

    emerald: {
        50: '#ECFDF5',
        100: '#D1FAE5',
        200: '#A7F3D0',
        300: '#6EE7B7',
        400: '#34D399',
        500: '#10B981',
        600: '#059669',
        700: '#047857',
        800: '#065F46',
        900: '#064E3B',
        950: '#022C22'
    },

    lime: {
        50: '#F7FEE7',
        100: '#ECFCCB',
        200: '#D9F99D',
        300: '#BEF264',
        400: '#A3E635',
        500: '#84CC16',
        600: '#65A30D',
        700: '#4D7C0F',
        800: '#3F6212',
        900: '#365314',
        950: '#1A2E05'
    },

    amber: {
        50: '#FFFBEB',
        100: '#FEF3C7',
        200: '#FDE68A',
        300: '#FCD34D',
        400: '#FBBF24',
        500: '#F59E0B',
        600: '#D97706',
        700: '#B45309',
        800: '#92400E',
        900: '#78350F',
        950: '#451A03'
    },

    zinc: {
        50: '#FAFAFA',
        100: '#F4F4F5',
        200: '#E4E4E7',
        300: '#D4D4D8',
        400: '#A1A1AA',
        500: '#71717A',
        600: '#52525B',
        700: '#3F3F46',
        800: '#27272A',
        900: '#18181B',
        950: '#09090B'
    },

    neutral: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#E5E5E5',
        300: '#D4D4D4',
        400: '#A3A3A3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        950: '#0A0A0A'
    },

    stone: {
        50: '#FAFAF9',
        100: '#F5F5F4',
        200: '#E7E5E4',
        300: '#D6D3D1',
        400: '#A8A29E',
        500: '#78716C',
        600: '#57534E',
        700: '#44403C',
        800: '#292524',
        900: '#1C1917',
        950: '#0C0A09'
    },

    // ===== Cleverly DS scale-name aliases over the old ramps =====
    // (component tokens reference sand/grey/wine; map them to the old grays/reds)
    sand: {
        50: '#FEFAF7',
        100: '#F6F0EC',
        200: '#F1EAE4',
        300: '#EAE3DD',
        400: '#DED4CE',
        500: '#9CA3AF',
        600: '#6B7280',
        700: '#4B5563',
        800: '#3B3B3B',
        900: '#1C1C1C',
        950: '#111827'
    },

    grey: {
        50: '#F5F5F5',
        100: '#ECECEC',
        200: '#D9D9D9',
        300: '#B3B3B3',
        400: '#949494',
        500: '#767676',
        600: '#4A4A4A',
        700: '#333333',
        800: '#1A1A1A',
        900: '#0E0E0E',
        950: '#000000'
    },

    wine: {
        50: '#FEF2F2',
        100: '#FEE2E2',
        200: '#FECACA',
        300: '#FCA5A5',
        400: '#FF3F61',
        500: '#FF4032',
        600: '#DC2626',
        700: '#B91C1C',
        800: '#991B1B',
        900: '#7F1D1D',
        950: '#450A0A'
    }
};

export const semantic: AuraBaseTokenSections.Semantic = {
    // ===== CORE SEMANTIC PROPERTIES =====
    transitionDuration: '{extend.transitionDuration.normal}',
    focusRing: {
        width: '2px',
        style: 'solid',
        color: '{blue.400}',
        offset: '2px',
        shadow: '0 0 0 2px rgba(53, 171, 226, 0.3)'
    },
    disabledOpacity: '0.3',
    iconSize: '1rem',
    anchorGutter: '0.25rem',

    // ===== PRIMARY COLOR SCALE =====
    primary: {
        50: '{blue.50}',
        100: '{blue.100}',
        200: '{blue.200}',
        300: '{blue.300}',
        400: '{blue.400}',
        500: '{blue.500}',
        600: '{blue.600}',
        700: '{blue.700}',
        800: '{blue.800}',
        900: '{blue.900}',
        950: '{blue.950}'
    },

    // ===== FORM FIELD CONFIGURATION =====
    formField: {
        paddingX: '{extend.inputpaddingx}',
        paddingY: '{extend.inputpaddingy}',
        sm: {
            fontSize: '0.875rem',
            paddingX: '0.75rem',
            paddingY: '0.5rem'
        },
        lg: {
            fontSize: '1.125rem',
            paddingX: '1.25rem',
            paddingY: '0.875rem'
        },
        borderRadius: '{borderRadius.xs}',
        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{blue.400}',
            offset: '0px',
            shadow: `0 0 0 2px ${hexWithAlpha(primitive.blue[500], 0.3)}`
        },
        transitionDuration: '{extend.transitionDuration.normal}'
    },

    // ===== LIST CONFIGURATION =====
    list: {
        padding: '0.5rem',
        gap: '0.125rem',
        header: {
            padding: '0.75rem 1rem'
        },
        option: {
            padding: '0.75rem 1rem',
            borderRadius: '{borderRadius.sm}'
        },
        optionGroup: {
            padding: '0.75rem 1rem',
            fontWeight: '600'
        }
    },

    // ===== CONTENT CONFIGURATION =====
    content: {
        borderRadius: '{borderRadius.lg}'
    },

    // ===== MASK CONFIGURATION =====
    mask: {
        transitionDuration: '{extend.transitionDuration.normal}'
    },

    // ===== NAVIGATION CONFIGURATION =====
    navigation: {
        list: {
            padding: '0.5rem',
            gap: '0.125rem'
        },
        item: {
            padding: '0.75rem 1rem',
            borderRadius: '{borderRadius.sm}',
            gap: '0.5rem'
        },
        submenuLabel: {
            padding: '0.75rem 1rem',
            fontWeight: '300'
        },
        submenuIcon: {
            size: '0.875rem'
        }
    },

    // ===== OVERLAY CONFIGURATION =====
    overlay: {
        select: {
            borderRadius: '{borderRadius.lg}',
            shadow: '{extend.boxshadowlayer1}'
        },
        popover: {
            borderRadius: '{borderRadius.lg}',
            padding: '1rem',
            shadow: '{extend.boxshadowlayer1}'
        },
        modal: {
            borderRadius: '{borderRadius.lg}',
            padding: '1.5rem',
            shadow: '{extend.boxshadowlayer1}'
        },
        navigation: {
            shadow: '{extend.boxshadowlayer1}'
        }
    },

    // ===== COLOR SCHEME (LIGHT ONLY) =====
    colorScheme: {
        light: {
            // Surface color scale
            surface: {
                0: '#FFFFFF',
                50: '{gray.50}',
                100: '{gray.100}',
                200: '{gray.200}',
                300: '{gray.300}',
                400: '{gray.400}',
                500: '{gray.500}',
                600: '{gray.600}',
                700: '{gray.700}',
                800: '{gray.800}',
                900: '{gray.900}',
                950: '{gray.950}'
            },

            // Primary colors
            primary: {
                color: '{blue.500}',
                contrastColor: '{gray.50}',
                hoverColor: '{blue.300}',
                activeColor: '{blue.500}'
            },

            // Highlight configuration
            highlight: {
                background: `${hexWithAlpha(primitive.blue[500], 0.1)}`,
                focusBackground: `${hexWithAlpha(primitive.blue[500], 0.2)}`,
                color: '{blue.700}',
                focusColor: '{blue.800}'
            },

            // Mask configuration
            mask: {
                background: '{extend.maskbg}',
                color: '{gray.50}'
            },

            // Form field colors
            formField: {
                background: '{gray.100}',
                disabledBackground: '{extend.inputbackgrounddisabled}',
                filledBackground: '{gray.100}',
                filledHoverBackground: '{gray.200}',
                filledFocusBackground: '{gray.50}',
                borderColor: '{gray.300}',
                hoverBorderColor: '{gray.400}',
                focusBorderColor: '{extend.inputbordercolorfocus}',
                invalidBorderColor: '{extend.stateerror}',
                color: '{gray.900}',
                disabledColor: '{extend.textcolordisabled}',
                placeholderColor: '{gray.500}',
                invalidPlaceholderColor: '{extend.stateerror}',
                floatLabelColor: '{text.hoverMutedColor}',
                floatLabelFocusColor: '{text.color}',
                floatLabelActiveColor: '{text.color}',
                floatLabelInvalidColor: '{extend.stateerror}',
                iconColor: '{gray.500}',
                shadow: 'none'
            },

            // Text colors
            text: {
                color: '{gray.900}',
                hoverColor: '{gray.900}',
                mutedColor: '{gray.800}',
                hoverMutedColor: '{gray.500}'
            },

            // Content colors
            content: {
                background: '#FFFFFF',
                hoverBackground: '{gray.100}',
                borderColor: '{gray.200}',
                color: '{gray.900}',
                hoverColor: '{gray.800}'
            },

            // Overlay colors
            overlay: {
                select: {
                    background: '{gray.50}',
                    borderColor: '{gray.200}',
                    color: '{gray.900}'
                },
                popover: {
                    background: '{gray.50}',
                    borderColor: '{gray.200}',
                    color: '{gray.900}'
                },
                modal: {
                    background: '{gray.50}',
                    borderColor: '{gray.200}',
                    color: '{gray.900}'
                }
            },

            // List colors
            list: {
                option: {
                    focusBackground: '{gray.100}',
                    selectedBackground: '{blue.100}',
                    selectedFocusBackground: '{blue.200}',
                    color: '{gray.900}',
                    focusColor: '{gray.900}',
                    selectedColor: '{blue.700}',
                    selectedFocusColor: '{blue.800}',
                    icon: {
                        color: '{gray.500}',
                        focusColor: '{gray.600}'
                    }
                },
                optionGroup: {
                    background: 'none',
                    color: '{blue.500}'
                }
            },

            // Navigation colors
            navigation: {
                item: {
                    focusBackground: '{gray.100}',
                    activeBackground: '{blue.100}',
                    color: '{gray.700}',
                    focusColor: '{gray.800}',
                    activeColor: '{blue.700}',
                    icon: {
                        color: '{gray.500}',
                        focusColor: '{gray.600}',
                        activeColor: '{blue.600}'
                    }
                },
                submenuLabel: {
                    background: 'none',
                    color: '{blue.500}'
                },
                submenuIcon: {
                    color: '{gray.500}',
                    focusColor: '{gray.600}',
                    activeColor: '{blue.600}'
                }
            }
        }
    }
};

export default {
    primitive,
    semantic
} satisfies AuraBaseDesignTokens;


function hexToRgb(hex: string): string {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `${r}, ${g}, ${b}`;
}

function hexWithAlpha(hex: string, alpha: number): string {
    // returns 8-digit hex like #RRGGBBAA
    const h = hex.replace('#', '');
    const rrggbb = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
               .toString(16)
               .padStart(2, '0')
               .toUpperCase();
    return `#${rrggbb}${a}`;
}